function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { reject, resolve } from 'rsvp';
import { getOwner } from '@ember/application';
import { assert } from '@ember/debug';
import { setProperties } from '@ember/object';
import Service from '@ember/service';
import { Attribute } from '@square/experiments/protos/squareup/feature/relay/common/attribute';
import { Token } from '@square/experiments/protos/squareup/feature/relay/common/token';
import { UserAttributes } from '@square/experiments/protos/squareup/feature/relay/common/user_attributes';
import { GetActivateExperimentRequest } from '@square/experiments/protos/squareup/feature/relay/experiments/message/get_activate_experiment';
import { GetExperimentRequest, GetExperimentsRequest } from '@square/experiments/protos/squareup/feature/relay/experiments/message/get_experiments';
import { Status } from '@square/experiments/protos/squareup/feature/relay/experiments/message/status';
import { TrackEventRequest } from '@square/experiments/protos/squareup/feature/relay/experiments/message/track_event';
import { ExperimentsService as ExperimentsServiceProto } from '@square/experiments/protos/squareup/feature/relay/experiments/service';
import rpc from '@square/experiments/utils/rpc';
import { getConsent, OneTrustConsent } from '@square/dashboard-utilities/utils/one-trust/one-trust-compliance';
const ONETRUST_CONSENT_LEVEL = OneTrustConsent.PERFORMANCE;
export default class SquareExperiments extends Service {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "baseAttributes", void 0);
    _defineProperty(this, "cdpKey", void 0);
    _defineProperty(this, "projectId", void 0);
  }
  get client() {
    return ExperimentsServiceProto.create(rpc((_method, guessedUrl) => guessedUrl));
  }
  initialize({
    projectId,
    baseAttributes,
    cdpKey
  }) {
    setProperties(this, {
      projectId,
      baseAttributes,
      cdpKey
    });
  }
  async getActivateExperiment({
    experimentName,
    token,
    additionalUserAttributes,
    cdpKey,
    projectId,
    tokenType = Token.Type.MERCHANT
  }) {
    // wait for onetrust consent to be provided
    const hasConsent = await getConsent(getOwner(this), ONETRUST_CONSENT_LEVEL);
    if (!hasConsent) {
      return reject(new Error('Experiment Relay: Performance tracking consent was rejected'));
    }
    const chosenProjectId = projectId || this.projectId;
    const chosenCdpKey = cdpKey || this.cdpKey;
    assert('A projectId must be defined', chosenProjectId);
    const mergedUserAttributes = Object.assign({}, this.baseAttributes, additionalUserAttributes);
    try {
      const {
        status,
        variantName
      } = await this.client.getActivateExperiment(GetActivateExperimentRequest.create({
        cdpKey: chosenCdpKey,
        experimentName,
        projectId: chosenProjectId,
        userAttributes: Object.keys(mergedUserAttributes).length > 0 ? toUserAttributes(mergedUserAttributes) : undefined,
        userToken: Token.create({
          token,
          type: tokenType
        })
      }));
      if ([Status.SUCCESS, Status.IMPRESSION_LIMIT_EXCEEDED].includes(status)) {
        return resolve(variantName);
      } else {
        return reject(new Error(`getActivateExperiment failed. Status: ${status}`));
      }
    } catch (e) {
      return reject(e);
    }
  }
  async getExperiment({
    experimentName,
    token,
    additionalUserAttributes,
    projectId,
    tokenType = Token.Type.MERCHANT
  }) {
    // wait for onetrust consent to be provided
    const hasConsent = await getConsent(getOwner(this), ONETRUST_CONSENT_LEVEL);
    if (!hasConsent) {
      throw new Error('Experiment Relay: Performance tracking consent was rejected');
    }
    const chosenProjectId = projectId || this.projectId;
    assert('A projectId must be defined', chosenProjectId);
    const mergedUserAttributes = Object.assign({}, this.baseAttributes, additionalUserAttributes);
    const {
      experimentResponses
    } = await this.client.getExperiments(GetExperimentsRequest.create({
      experimentRequests: [GetExperimentRequest.create({
        experimentName,
        userToken: Token.create({
          token,
          type: tokenType
        })
      })],
      projectId: chosenProjectId,
      userAttributes: Object.keys(mergedUserAttributes).length > 0 ? toUserAttributes(mergedUserAttributes) : undefined
    }));
    if (experimentResponses?.length === 0) {
      throw new Error('getExperiment failed. No response');
    }
    return experimentResponses[0];
  }
  async trackEvent({
    eventName,
    token,
    additionalUserAttributes,
    cdpKey,
    eventAttributes,
    projectId,
    tokenType = Token.Type.MERCHANT
  }) {
    // wait for onetrust consent to be provided
    const hasConsent = await getConsent(getOwner(this), ONETRUST_CONSENT_LEVEL);
    if (!hasConsent) {
      return reject(new Error('Experiment Relay: Performance tracking consent was rejected'));
    }
    const chosenProjectId = projectId || this.projectId;
    const chosenCdpKey = cdpKey || this.cdpKey;
    assert('A projectId must be defined', chosenProjectId);
    const mergedUserAttributes = Object.assign({}, this.baseAttributes, additionalUserAttributes);
    try {
      const trackEventResponse = await this.client.trackEvent(TrackEventRequest.create({
        cdpKey: chosenCdpKey,
        eventName,
        eventValues: eventAttributes ? toAttributesArray(eventAttributes) : undefined,
        projectId: chosenProjectId,
        userAttributes: Object.keys(mergedUserAttributes).length > 0 ? toUserAttributes(mergedUserAttributes) : undefined,
        userToken: Token.create({
          token,
          type: tokenType
        })
      }));
      if (trackEventResponse.status !== Status.SDK_ERROR) {
        return resolve(trackEventResponse);
      } else {
        return reject(new Error(`trackEvent failed. Status: ${status}`));
      }
    } catch (e) {
      return reject(e);
    }
  }
}
function toUserAttributes(additionalUserAttributes) {
  return UserAttributes.create({
    userAttributes: toAttributesArray(additionalUserAttributes)
  });
}
function toAttributesArray(additionalUserAttributes) {
  return Object.entries(additionalUserAttributes).map(([key, value]) => {
    if (typeof value === 'boolean') {
      return Attribute.create({
        name: key,
        boolValue: value
      });
    } else if (typeof value === 'number') {
      if (isInt(value)) {
        return Attribute.create({
          name: key,
          intValue: value
        });
      } else {
        return Attribute.create({
          name: key,
          doubleValue: value
        });
      }
    } else {
      return Attribute.create({
        name: key,
        stringValue: value
      });
    }
  });
}
function isInt(num) {
  return Math.trunc(num) === num;
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.